import * as React from 'react';
import { Check, Eye, Send } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { AreaChip } from '@/components/ui/area-chip';
import {
  FilterChip,
  SegmentedControl,
  WorkspaceButton,
  WorkspaceInput,
  useToast,
  workspaceInputClass,
} from '@/components/ui/workspace';
import { useCoverageQuery } from '@/lib/api/discovery';
import {
  sendInterviewQuestionnaire,
  useDiscoveryWrite,
  useInterviewQuestionnaireQuery,
  useRecordLiveAnswer,
  type InterviewDetail,
  type RunQuestion,
} from '@/lib/api/discovery-sessions';
import { cn } from '@/lib/utils/cn';
import { errorText } from './errors';
import { LinkRow, QuestionnairePreview } from './link-bits';
import { answerValue, firstName, longDay, type AnswerDraft } from './sessions-logic';

/**
 * Step 2, the questionnaire (E12-F08): before the interview it can be sent
 * ahead by secure link; once held, answers are typed in live and each saved
 * answer is confirmed evidence from the interviewee, worded as asked
 * (rule 5). The rest can go by secure link. Prototype: `questBody`.
 */
export function QuestionnaireStep({
  engagementId,
  d,
  canExtend,
}: {
  engagementId: string;
  d: InterviewDetail;
  canExtend: boolean;
}) {
  const { toast } = useToast();
  const q = useInterviewQuestionnaireQuery(engagementId, d.interview.id, true);
  const coverage = useCoverageQuery(engagementId);
  const [preview, setPreview] = React.useState(false);
  const [seeAnswers, setSeeAnswers] = React.useState(false);
  const send = useDiscoveryWrite(engagementId, () =>
    sendInterviewQuestionnaire(engagementId, d.interview.id),
  );

  if (q.error) return <ErrorMessage error={q.error} />;
  if (!q.data) {
    return (
      <div className="flex items-center gap-2 text-meta text-ink-500">
        <Spinner /> Loading the questionnaire…
      </div>
    );
  }
  const r = q.data;
  const held = d.interview.status === 'held';
  const name = r.person.full_name;
  const labelOf = (id: string) => coverage.data?.areas.find((a) => a.area_id === id)?.label ?? '';
  const live = r.link?.status === 'live';
  const allDone = r.answered >= r.total;

  const previewButton = (
    <WorkspaceButton
      variant="tertiary"
      onClick={() => setPreview((v) => !v)}
      aria-expanded={preview}
    >
      <Eye className="h-3.5 w-3.5" aria-hidden="true" /> {preview ? 'Hide preview' : 'Preview'}
    </WorkspaceButton>
  );
  const sendButton =
    live || allDone ? null : (
      <WorkspaceButton
        disabled={send.isPending}
        onClick={() => {
          if (!r.person.email) {
            toast({
              message: `Add ${firstName(name)}'s email first, in People.`,
              tone: 'critical',
            });
            return;
          }
          send.mutate(undefined, {
            onSuccess: (s) =>
              toast({
                message: `Secure link sent to ${name} for ${
                  held ? 'the unanswered questions' : 'the questionnaire'
                }. It expires ${longDay(s.expires_at)}.`,
              }),
            onError: (e) => toast({ message: errorText(e), tone: 'critical' }),
          });
        }}
      >
        <Send className="h-3.5 w-3.5" aria-hidden="true" />{' '}
        {held ? 'Send the rest by secure link' : 'Send ahead'}
      </WorkspaceButton>
    );
  const link = r.link ? (
    <LinkRow engagementId={engagementId} link={r.link} who={name} canExtend={canExtend} />
  ) : null;
  const previewBox = preview ? (
    <QuestionnairePreview engagementId={engagementId} questionnaireId={r.questionnaire.id} />
  ) : null;
  const answered = r.questions.filter((x) => x.answer);

  if (allDone) {
    return (
      <>
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="min-w-0 flex-1 text-body-sm text-ink-700">
            All {r.total} answered by {name}. Each answer is confirmed evidence.
          </span>
          <WorkspaceButton onClick={() => setSeeAnswers((v) => !v)} aria-expanded={seeAnswers}>
            {seeAnswers ? 'Hide the answers' : 'See the answers'}
          </WorkspaceButton>
          {previewButton}
        </div>
        {seeAnswers ? <AnsweredRows questions={answered} /> : null}
        {previewBox}
      </>
    );
  }

  if (!held) {
    return (
      <>
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="min-w-0 flex-1 text-body-sm text-ink-700">
            {r.total} questions, about {r.questionnaire.minutes} minutes. Answer them together in
            the session, or send them ahead so the session can go deeper.
          </span>
          {previewButton}
          {sendButton}
        </div>
        {link}
        {previewBox}
      </>
    );
  }

  return (
    <>
      <div className="mb-2.5 flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div
            className="mb-1 h-1.5 max-w-[320px] overflow-hidden rounded-full bg-ink-100"
            role="progressbar"
            aria-label="Answered"
            aria-valuemin={0}
            aria-valuemax={r.total}
            aria-valuenow={r.answered}
          >
            <i
              className="block h-full rounded-full bg-accent-amber-600"
              style={{ width: `${(r.answered / Math.max(1, r.total)) * 100}%` }}
            />
          </div>
          <div className="text-meta text-ink-500">
            {r.answered} of {r.total} answered with {name}. Each saved answer becomes confirmed
            evidence from {firstName(name)}.
          </div>
        </div>
        {previewButton}
        {sendButton}
      </div>
      {link}
      {previewBox}
      <AnsweredRows questions={answered} />
      {r.questions
        .filter((x) => !x.answer)
        .map((x) => (
          <LiveQuestion
            key={x.run_question_id}
            engagementId={engagementId}
            interviewId={d.interview.id}
            question={x}
            personName={name}
            labelOf={labelOf}
          />
        ))}
    </>
  );
}

function AnsweredRows({ questions }: { questions: RunQuestion[] }) {
  if (questions.length === 0) return null;
  return (
    <div className="mt-2">
      {questions.map((x) => (
        <div
          key={x.run_question_id}
          className="flex items-start gap-2.5 border-b border-ink-100 px-0.5 py-[7px] text-body-sm"
        >
          <span className="min-w-0 flex-1">
            <span className="block text-meta text-ink-500">{x.prompt}</span>
            <span className="text-ink-900">{x.answer!.text}</span>
          </span>
          {x.answer!.evidence_number ? (
            <span className="font-mono text-meta text-ink-700">{x.answer!.evidence_number}</span>
          ) : (
            <span className="text-meta text-ink-500">Held</span>
          )}
          <Check className="mt-0.5 h-3.5 w-3.5 flex-none text-posture-healthy" aria-hidden="true" />
        </div>
      ))}
    </div>
  );
}

function LiveQuestion({
  engagementId,
  interviewId,
  question: x,
  personName,
  labelOf,
}: {
  engagementId: string;
  interviewId: string;
  question: RunQuestion;
  personName: string;
  labelOf: (areaId: string) => string;
}) {
  const { toast } = useToast();
  const [draft, setDraft] = React.useState<AnswerDraft | undefined>(undefined);
  // The saved answer and the count land in the questionnaire straight from
  // the write's result; the rest of discovery is read again in the
  // background.
  const save = useRecordLiveAnswer(engagementId, interviewId);
  const value = answerValue(x.answer_type, draft);
  const onSave = () => {
    if (!value) {
      toast({ message: 'Type or pick an answer first.' });
      return;
    }
    // Once saved, this card leaves (the answer moves up to the answered
    // rows), so the confirmation hangs off the promise rather than the
    // mutate callbacks, which stop firing when the card unmounts.
    save.mutateAsync({ run_question_id: x.run_question_id, value }).then(
      (res) => {
        const areas = res.evidence.area_ids.map(labelOf).filter(Boolean);
        toast({
          message: res.evidence.held
            ? `Saved. It counts once ${personName}'s possible duplicate is resolved.`
            : `Saved as ${res.evidence.number}${
                areas.length > 0
                  ? `, counting toward ${areas.join(', ')}`
                  : '. Not tagged to an area, so it counts once tagged'
              }.`,
        });
      },
      (e: unknown) => toast({ message: errorText(e), tone: 'critical' }),
    );
  };

  return (
    <div className="mb-2 mt-3 rounded-card border border-ink-100 bg-paper-0 px-4 py-3.5">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-body-sm font-medium text-ink-900">{x.prompt}</div>
          <div className="mb-2 mt-1.5 flex flex-wrap gap-1">
            {x.area_ids.length === 0 ? (
              <AreaChip all />
            ) : (
              x.area_ids.map((a) => <AreaChip key={a}>{labelOf(a)}</AreaChip>)
            )}
          </div>
          <AnswerInput question={x} draft={draft} onChange={setDraft} />
        </div>
        <WorkspaceButton disabled={save.isPending} onClick={onSave}>
          {save.isPending ? 'Saving…' : 'Save'}
        </WorkspaceButton>
      </div>
    </div>
  );
}

function AnswerInput({
  question: x,
  draft,
  onChange,
}: {
  question: RunQuestion;
  draft: AnswerDraft | undefined;
  onChange: (d: AnswerDraft) => void;
}) {
  switch (x.answer_type) {
    case 'rating':
      return (
        <SegmentedControl
          aria-label={x.prompt}
          value={typeof draft === 'number' ? String(draft) : null}
          onChange={(v) => onChange(Number(v))}
          options={['1', '2', '3', '4', '5'].map((n) => ({ value: n, label: n }))}
        />
      );
    case 'single':
    case 'multiple': {
      const picked = Array.isArray(draft) ? draft : typeof draft === 'string' ? [draft] : [];
      return (
        <div className="flex flex-wrap gap-1.5" role="group" aria-label={x.prompt}>
          {x.options.map((o) => (
            <FilterChip
              key={o}
              on={picked.includes(o)}
              onClick={() =>
                onChange(
                  x.answer_type === 'single'
                    ? o
                    : picked.includes(o)
                      ? picked.filter((p) => p !== o)
                      : [...picked, o],
                )
              }
            >
              {o}
            </FilterChip>
          ))}
        </div>
      );
    }
    case 'number':
      return (
        <WorkspaceInput
          aria-label={x.prompt}
          className="max-w-[560px]"
          placeholder="A number, or a range"
          value={typeof draft === 'string' ? draft : ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    default:
      return (
        <textarea
          aria-label={x.prompt}
          rows={2}
          className={cn(workspaceInputClass, 'h-auto max-w-[560px] py-2')}
          placeholder="Type the answer as they say it"
          value={typeof draft === 'string' ? draft : ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}
